import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, User, ShoppingBag, Menu, X } from 'lucide-react';
import { useCart } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItemCount } = useCart();

  const needsSolidHeader = ['/membership', '/contact', '/cart', '/about'].some(
    path => location.pathname.startsWith(path)
  ) || location.pathname.startsWith('/products');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  }, [location]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/products?q=${encodeURIComponent(searchQuery.trim())}`);
        const data = await res.json();
        setSearchResults(data);
      } catch (e) {
        console.error('Search error:', e);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery]);

  const handleSearchToggle = () => {
    setIsSearchOpen(!isSearchOpen);
    setSearchQuery('');
    setSearchResults([]);
    if (!isSearchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/products?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
    }
    if (e.key === 'Escape') {
      setIsSearchOpen(false);
    }
  };

  const navLinks = [
    { name: 'COLLECTIONS', path: '/products' },
    { name: 'STORY', path: '/about' },
    { name: 'THE CLUB', path: '/membership' },
    { name: 'CONTACT', path: '/contact' },
  ];

  const showSolidBg = isScrolled || needsSolidHeader;

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          showSolidBg
            ? 'bg-[#050505]/98 backdrop-blur-md border-b border-[#D4AF37]/20'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Left Navigation - Desktop */}
            <nav className="hidden md:flex items-center space-x-8 flex-1" data-testid="desktop-nav">
              {navLinks.slice(0, 2).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  className={`font-heading text-xs tracking-[0.2em] transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'text-[#D4AF37]'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-button"
              className="md:hidden text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Center Logo */}
            <Link to="/" data-testid="logo-link" className="flex-shrink-0">
              <h1 className="text-xl md:text-2xl tracking-[0.1em] text-white" style={{ fontFamily: "'Aref Ruqaa', serif", fontWeight: 700 }}>
                QASR <span className="text-[#D4AF37]">PARFUMERIE</span>
              </h1>
            </Link>

            {/* Right Navigation - Desktop */}
            <nav className="hidden md:flex items-center justify-end space-x-8 flex-1" data-testid="desktop-nav-right">
              {navLinks.slice(2).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  className={`font-heading text-xs tracking-[0.2em] transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'text-[#D4AF37]'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Utility Icons */}
            <div className="flex items-center space-x-4 md:space-x-6 ml-6">
              <button
                data-testid="search-button"
                onClick={handleSearchToggle}
                className="text-white/70 hover:text-[#D4AF37] transition-colors duration-200"
              >
                <Search size={20} />
              </button>
              <Link
                to="/membership"
                data-testid="account-link"
                className="hidden md:block text-white/70 hover:text-[#D4AF37] transition-colors duration-200"
              >
                <User size={20} />
              </Link>
              <Link
                to="/cart"
                data-testid="cart-link"
                className="relative text-white/70 hover:text-[#D4AF37] transition-colors duration-200"
              >
                <ShoppingBag size={20} />
                {cartItemCount > 0 && (
                  <span
                    data-testid="cart-count"
                    className="absolute -top-2 -right-2 w-5 h-5 bg-[#D4AF37] text-[#050505] text-xs font-bold flex items-center justify-center"
                  >
                    {cartItemCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>

        {/* Search Bar with Results */}
        {isSearchOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#050505]/98 backdrop-blur-md border-b border-[#D4AF37]/20" data-testid="search-panel">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  data-testid="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search fragrances, notes, categories..."
                  className="w-full bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] text-white placeholder:text-white/30 py-4 outline-none font-body text-lg"
                  autoFocus
                />
                <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-3">
                  {isSearching && (
                    <div className="w-4 h-4 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
                  )}
                  <button onClick={handleSearchToggle} className="text-white/40 hover:text-white/70 transition-colors">
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Search Results Dropdown */}
              {searchQuery.trim().length >= 2 && (
                <div className="py-4 max-h-[60vh] overflow-y-auto" data-testid="search-results">
                  {searchResults.length > 0 ? (
                    <>
                      <p className="font-heading text-[10px] tracking-[0.2em] text-[#8a7a60] mb-4">
                        {searchResults.length} RESULT{searchResults.length !== 1 ? 'S' : ''} FOUND
                      </p>
                      <div className="space-y-2">
                        {searchResults.map(product => (
                          <Link
                            key={product.id}
                            to={`/products/${product.id}`}
                            data-testid={`search-result-${product.id}`}
                            className="flex items-center gap-4 p-3 transition-colors duration-200 hover:bg-white/5 group"
                            onClick={() => setIsSearchOpen(false)}
                          >
                            <div className="w-14 h-14 flex-shrink-0 overflow-hidden" style={{
                              border: '1px solid #3d3428',
                              background: '#080604',
                            }}>
                              <img src={product.image} alt={product.name} className="w-full h-full object-cover" style={{ filter: 'sepia(0.1) brightness(0.9)' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-heading text-sm tracking-wider text-white/80 group-hover:text-[#a89878] transition-colors truncate">{product.name}</h4>
                              <p className="font-body text-xs text-white/30">{product.subtitle} &middot; {product.category}</p>
                            </div>
                            <div className="flex flex-col items-end flex-shrink-0">
                              {product.original_price && product.original_price > product.price && (
                                <span className="font-body text-[10px] text-white/30 line-through">${product.original_price.toFixed(0)}</span>
                              )}
                              <span className="font-heading text-sm" style={{ color: product.discount_pct ? '#D4AF37' : '#8a7a60' }}>${product.price?.toFixed(2)}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                      {searchQuery.trim() && (
                        <button
                          onClick={() => {
                            navigate(`/products?q=${encodeURIComponent(searchQuery.trim())}`);
                            setIsSearchOpen(false);
                          }}
                          className="mt-4 w-full text-center py-3 font-heading text-xs tracking-[0.15em] transition-colors"
                          style={{ border: '1px solid #3d3428', color: '#8a7a60' }}
                          data-testid="search-view-all"
                        >
                          VIEW ALL RESULTS
                        </button>
                      )}
                    </>
                  ) : !isSearching ? (
                    <div className="py-8 text-center">
                      <p className="font-body text-white/30 text-sm">No fragrances found for "{searchQuery}"</p>
                      <p className="font-body text-white/15 text-xs mt-1">Try searching by name, category, or scent notes</p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-[#050505]/98 backdrop-blur-md md:hidden"
          data-testid="mobile-menu"
        >
          <div className="absolute inset-0 arabian-pattern opacity-10" />
          
          <nav className="relative flex flex-col items-center justify-center h-full space-y-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`font-heading text-2xl tracking-[0.2em] ${
                  location.pathname === link.path ? 'text-[#D4AF37]' : 'text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            <p className="font-arabic text-xl text-[#D4AF37]/50 mt-8">
              عطور فاخرة
            </p>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
